import { Phone, MapPin, ShieldCheck, CloudRainWind, Home, Layers } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import { IMAGES, PACKAGES } from '../data/content';

const NEEDS = [
  {
    icon: ShieldCheck,
    title: 'High-cost work needs visible trust',
    text: 'A new roof is one of the most expensive jobs a homeowner ever commissions — and the trade with the worst cowboy reputation. Insurance, guarantees and your real project photos, displayed prominently, separate you from the fly-by-nights.',
  },
  {
    icon: CloudRainWind,
    title: 'Storm damage searches spike without warning',
    text: 'When bad weather hits, searches for roofers explode overnight. A complete Google Business Profile and a fast website mean you’re the one who’s findable when it matters most.',
  },
  {
    icon: Phone,
    title: 'Worried homeowners can reach you instantly',
    text: 'A leaking roof is urgent and stressful. Click-to-call and WhatsApp on every page — thumb-height on mobile — means the panicked customer rings you, not the next result.',
  },
];

const WORK_TYPES = [
  {
    icon: CloudRainWind,
    title: 'Storm and emergency repairs',
    text: 'Slipped tiles, leaks and storm damage — urgent jobs won by being findable, credible and contactable in the same minute.',
  },
  {
    icon: Home,
    title: 'Re-roofs and replacements',
    text: 'The biggest jobs in the trade, researched for weeks. A gallery of finished re-roofs and genuine reviews wins the shortlist before you ever climb a ladder.',
  },
  {
    icon: Layers,
    title: 'Flat roofs, fascias and guttering',
    text: 'The steady bread-and-butter work that fills gaps between big jobs — a clear services list makes sure customers know you cover it all.',
  },
];

const ROOFER_FAQS = [
  {
    q: 'Roofing has a cowboy problem. How does a website help with that?',
    a: 'Directly. Homeowners paying thousands for a re-roof check everything: reviews, photos of finished work, insurance, how long you’ve traded. A professional website that shows all of it — clearly, without waffle — is the single strongest signal that you’re one of the good ones. We build the site around exactly that.',
  },
  {
    q: 'Can you set me up to catch storm damage work?',
    a: 'Your Google Business Profile and website will make your services, areas and contact details crystal clear — exactly what storm-damage searches need. What nobody can honestly promise is a specific position in Google’s results; we set everything up to give you the best genuine chance, and the groundwork is done before the weather turns.',
  },
  {
    q: 'I only get photos when the lads remember. Is that enough?',
    a: 'Yes — a handful of decent phone photos is plenty to launch with, and we’ll show your crew the two or three shots worth taking on each job (scaffold-up wide shot, mid-work detail, finished roofline). The site is built so you can add fresh projects easily, or we can do it for you on a monthly plan.',
  },
  {
    q: 'I’m up on roofs all week. How much of my time does this take?',
    a: 'About an hour in total, spread across the build: one plain-English questionnaire, your project photos from your phone, and a look at the finished site before launch. Everything else is handled for you.',
  },
];

export default function RoofersPage() {
  return (
    <>
      <Seo
        title="Websites for Roofers — Win Re-Roofs & Storm Repairs | Build My Trade"
        description="Professional websites, Google Business Profile setup and social pages built specifically for UK roofers. Fixed packages from £249, plain English, everything handled."
        path="/websites-for-roofers"
        image={IMAGES.rooferHero.url}
      />

      {/* Hero */}
      <section className="bg-charcoal bg-blueprint" data-testid="roofers-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              Websites for roofers
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
              Big-ticket jobs go to roofers who <span className="text-lime">look established.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything a roofer needs to win re-roofs, storm repairs and steady local work:
              a professional website that proves you’re trustworthy, a complete Google Business Profile and branded
              social pages — for one fixed, published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="roofers-cta-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="roofers-cta-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
            <p className="mt-7 text-sm text-offwhite/50">
              Straightforward packages. Clear prices. No marketing jargon.
            </p>
          </div>
          <Reveal>
            <img
              src={IMAGES.rooferHero.url}
              alt={IMAGES.rooferHero.alt}
              loading="eager"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* What a roofer's online presence needs */}
      <section className="bg-offwhite" data-testid="roofers-needs">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Built around how roofing work is won
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              What a roofer’s website has to do
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {NEEDS.map((n, i) => (
              <Reveal key={i} delay={0.08 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                    <n.icon className="h-5 w-5 text-lime" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="border-y border-hairline bg-white" data-testid="roofers-work-types">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                  From emergency call-outs to full re-roofs
                </p>
                <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
                  Set up to win every kind of roofing job
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stonebrand">
                  Roofing work swings between urgent repairs and carefully planned replacements. Your website covers
                  both — instant contact details for the emergency, and the proof of quality that wins the big one.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <img
                  src={IMAGES.rooferDetail.url}
                  alt={IMAGES.rooferDetail.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {WORK_TYPES.map((w, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-5 rounded-2xl border border-hairline bg-offwhite-light p-7">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lime">
                        <w.icon className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{w.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-stonebrand">{w.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-charcoal" data-testid="roofers-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">Fixed, published prices</p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Packages that fit a roofing business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-offwhite/65">
              The same straightforward packages we offer every trade — one-off payments, everything listed, nothing
              hidden. Roofers chasing bigger re-roof work tend to choose Built to Grow: up to ten pages, an expanded
              portfolio and your branding sorted at the same time.
            </p>
          </Reveal>
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* Roofer FAQs */}
      <section className="bg-offwhite" data-testid="roofers-faqs">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Questions roofers ask us
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Straight answers for busy roofers
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion items={ROOFER_FAQS} testid="roofers-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="roofers-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Ready to be the roofer homeowners <span className="text-lime">trust with thousands?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your roofing business and the areas you cover — we’ll recommend the right starting
                point, in plain English, with no obligation.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="roofers-cta-final" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
